import { Attachment } from '@dixa/lib-shared-habla';
import FileSaver from 'file-saver';
import { AsyncStatus, MessageAttachedFile } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { Component, Dispatch, ReactNode } from 'react';
import { connect } from 'react-redux';

import { ajaxSend } from '../../../../../api/api';
import {
  AttachmentFile,
  isPreviewableAttachment,
  prepareAttachments,
} from '../../../../../app/utils/attachments';
import { actions } from '../../../../shared/attachmentsPreviewModal/actions/attachmentsPreviewModal';

const { attachmentsPreviewModalOpen } = actions;

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    previewImage: (payload) => dispatch(attachmentsPreviewModalOpen(payload)),
  };
};

type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type AttachedFileProps = {
  /** The url pointing to the file. */
  fileUrl: string;
  /** A human readable file. */
  prettyName?: string;
} & DispatchProps;

type AttachedFileState = {
  /** The status of the download operation. */
  downloadAsyncStatus: AsyncStatus;
  /** The status of the preview operation. */
  previewAsyncStatus: AsyncStatus;
};

export const AttachedFile = connect(
  null,
  mapDispatchToProps,
)(
  class AttachedFile extends Component<AttachedFileProps, AttachedFileState> {
    constructor(props: AttachedFileProps) {
      super(props);
      this.state = {
        downloadAsyncStatus: 'succeeded',
        previewAsyncStatus: 'succeeded',
      };
    }

    fetchPrivateFileBlob = (url: string): Promise<Blob> => {
      return ajaxSend<Blob>({
        path: url,
        responseType: 'blob',
      });
    };

    fetchPrivateFile = (url: string) => {
      this.setState({ downloadAsyncStatus: 'loading' });
      this.fetchPrivateFileBlob(url)
        .then((blob) => {
          const url = this.props.fileUrl;
          FileSaver.saveAs(
            blob,
            this.props.prettyName
              ? this.props.prettyName
              : decodeURIComponent(url.split('/').pop() as string),
          );
          this.setState({ downloadAsyncStatus: 'succeeded' });
          ampli.conversationAttachedImageIsDownloaded();
        })
        .catch((e) => {
          console.error(e);
          this.setState({ downloadAsyncStatus: 'failed' });
        });
    };

    previewPrivateFile = (fileName: string, url: string) => {
      const { previewImage } = this.props;
      this.setState({ previewAsyncStatus: 'loading' });
      this.fetchPrivateFileBlob(url)
        .then((blob) => window.URL.createObjectURL(blob))
        .then((blobUrl) => {
          this.setState({ previewAsyncStatus: 'succeeded' });
          previewImage({ fileName, url: blobUrl });
          ampli.conversationAttachedImageIsViewed();
        })
        .catch((e) => {
          console.error(e);
          this.setState({ previewAsyncStatus: 'failed' });
        });
    };

    render() {
      const { fileUrl, prettyName } = this.props;

      const fileName = prettyName
        ? encodeURIComponent(prettyName)
        : decodeURIComponent(fileUrl.split('/').pop() as string);

      const onPreviewHandler = isPreviewableAttachment(fileName)
        ? () => this.previewPrivateFile(fileName, fileUrl)
        : null;

      return (
        <Attachment
          fileName={fileName}
          isDownloadLoading={this.state.downloadAsyncStatus === 'loading'}
          isPreviewLoading={this.state.previewAsyncStatus === 'loading'}
          onDownload={() => this.fetchPrivateFile(fileUrl)}
          onPreview={
            onPreviewHandler
              ? () => (onPreviewHandler ? onPreviewHandler() : null)
              : undefined
          }
        />
      );
    }
  },
);

/**
 * Creates an array of `AttachedFile` components.
 *
 * @param attachedFiles The attached files.
 */
export function createAttachedFiles(
  attachedFiles: MessageAttachedFile | MessageAttachedFile[] | null | undefined,
): ReactNode[] {
  return prepareAttachments(attachedFiles).map((file: AttachmentFile) => (
    <AttachedFile
      prettyName={file.name}
      fileUrl={file.absolute_uri}
      key={file.absolute_uri}
    />
  ));
}
